/** A ligação à sala: websocket, reenvio e reconexão.
 *
 *  Separado do protocolo (que é puro) e do painel (que é React). Aqui
 *  só se trata do fio.
 *
 *  A reconexão existe porque uma reunião dura mais do que uma ligação
 *  de rede: um portátil que muda de wifi, um túnel, um servidor a
 *  reiniciar. Sem ela, a sala morria em silêncio e as pessoas ficavam
 *  a olhar para uma vista que já não mexe sem perceber porquê.
 */

import {
  type MensagemSala,
  mensagemValida,
} from '@viewer/room/roomProtocol'

/** O endereço do relé sai do mesmo sítio que o resto da API, com o
 *  esquema trocado para websocket. Em produção é wss; em
 *  desenvolvimento, contra um backend local em http, é ws. */
export function urlDaSala(
  salaId: string,
  nome: string,
  base: string = import.meta.env.VITE_API_BASE_URL ?? '',
): string {
  const raiz = base || window.location.origin
  const ws = raiz.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:')
  return `${ws.replace(/\/$/, '')}/api/room/${salaId}?nome=${encodeURIComponent(nome)}`
}

/** Códigos de fecho que são uma recusa do relé e não uma falha de rede.
 *  1008 = regra violada; 1013 = tentar mais tarde. Reconectar sozinho
 *  só repetia a recusa, por isso pára-se e mostra-se o motivo. */
export const CODIGOS_DE_RECUSA: ReadonlySet<number> = new Set([1008, 1013])

/** A recusa dita numa mensagem antes do fecho (backend/core/canais.py).
 *  Em produção, atrás do proxy, o fecho logo a seguir ao aceite não chega
 *  ao browser (fica 20 s parado e depois 1006, sem código): a mensagem
 *  chega. Devolve o código e o motivo, ou null se não for uma recusa. */
export function lerRecusa(lido: unknown): { codigo: number; motivo: string } | null {
  if (!lido || typeof lido !== 'object') return null
  const m = lido as Record<string, unknown>
  if (m.tipo !== 'recusado' || typeof m.codigo !== 'number') return null
  return { codigo: m.codigo, motivo: typeof m.motivo === 'string' ? m.motivo : '' }
}

export type EstadoLigacao = 'ligado' | 'a-ligar' | 'desligado'

export interface OpcoesLigacao {
  salaId: string
  nome: string
  aoReceber: (m: MensagemSala) => void
  /** O segundo argumento é quantas vezes seguidas já falhou. Serve
   *  para a interface poder distinguir "a rede oscilou" de "o servidor
   *  da sala não existe", que se parecem mas não se explicam da mesma
   *  maneira a quem está à espera. */
  aoMudarEstado: (e: EstadoLigacao, falhasSeguidas: number) => void
  /** O relé recusou a ligação (sala cheia, servidor sem salas, limite
   *  do endereço, identificador inválido). Não se volta a tentar: o
   *  motivo é o texto que o servidor mandou no fecho. */
  aoRecusar?: (motivo: string) => void
  /** Trocável nos testes. */
  criarSocket?: (url: string) => WebSocket
  base?: string
}

export interface LigacaoSala {
  enviar: (m: unknown) => void
  fechar: () => void
  estado: () => EstadoLigacao
}

/** Espera antes de voltar a tentar, a crescer até meio minuto.
 *
 *  Tentar de segundo a segundo contra um servidor em baixo é bater
 *  na porta sem parar; esperar demais faz a pessoa pensar que a sala
 *  morreu. */
export function esperaMs(tentativa: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.min(tentativa, 5))
}

export function ligarASala(opcoes: OpcoesLigacao): LigacaoSala {
  const criar = opcoes.criarSocket ?? ((url: string) => new WebSocket(url))
  let socket: WebSocket | null = null
  let estado: EstadoLigacao = 'a-ligar'
  let tentativa = 0
  let fechadoDeProposito = false
  let temporizador: number | undefined

  const mudar = (e: EstadoLigacao) => {
    estado = e
    opcoes.aoMudarEstado(e, tentativa)
  }

  const abrir = () => {
    if (fechadoDeProposito) return
    mudar('a-ligar')
    const url = urlDaSala(opcoes.salaId, opcoes.nome, opcoes.base)
    let s: WebSocket
    try {
      s = criar(url)
    } catch {
      agendarNova()
      return
    }
    socket = s

    s.onopen = () => {
      tentativa = 0
      mudar('ligado')
    }
    s.onmessage = (ev: MessageEvent) => {
      // O que chega do relé é dados e pode vir de qualquer
      // participante: valida-se antes de lhe tocar.
      try {
        const lido: unknown = JSON.parse(String(ev.data))
        const recusa = lerRecusa(lido)
        if (recusa) {
          if (CODIGOS_DE_RECUSA.has(recusa.codigo)) {
            fechadoDeProposito = true
            socket = null
            mudar('desligado')
            opcoes.aoRecusar?.(recusa.motivo || 'O servidor da sala recusou a ligação.')
            s.close()
          }
          return
        }
        if (mensagemValida(lido)) opcoes.aoReceber(lido)
      } catch {
        // Uma mensagem que não é JSON ignora-se em silêncio: não vale
        // a pena encher a consola por causa de um participante com
        // uma versão diferente.
      }
    }
    s.onclose = (ev?: CloseEvent) => {
      socket = null
      if (fechadoDeProposito) {
        mudar('desligado')
        return
      }
      if (ev && CODIGOS_DE_RECUSA.has(ev.code)) {
        fechadoDeProposito = true
        mudar('desligado')
        opcoes.aoRecusar?.(
          ev.reason || 'O servidor da sala recusou a ligação.',
        )
        return
      }
      agendarNova()
    }
    s.onerror = () => {
      // O `onclose` vem sempre a seguir: é lá que se trata da
      // reconexão, para não se agendar duas.
    }
  }

  const agendarNova = () => {
    mudar('desligado')
    const espera = esperaMs(tentativa)
    tentativa += 1
    temporizador = window.setTimeout(abrir, espera)
  }

  abrir()

  return {
    enviar: (m: unknown) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(m))
      }
      // Fora do ar não se guarda nada para mandar depois: uma câmara
      // de há dez segundos não interessa a ninguém, e uma selecção
      // volta a ser enviada na próxima que se fizer.
    },
    fechar: () => {
      fechadoDeProposito = true
      if (temporizador !== undefined) window.clearTimeout(temporizador)
      // Dizer que se sai, antes de fechar. Só com o fecho, o servidor
      // atrás do proxy demorava uns 12 segundos a dar a ligação por
      // morta, e os outros continuavam a ver-nos na sala.
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ tipo: 'saio' }))
      }
      socket?.close()
      socket = null
      mudar('desligado')
    },
    estado: () => estado,
  }
}
